import type { EventNames } from '@iheartjane/tracking';
import type { Nullable, SpecialType } from '@iheartjane/types';

type WizardCardLocation = 'wizard';
export type WizardOpenSource = 'banner' | 'wizard button';

export const WIZARD_SOURCE = {
  BANNER: 'banner',
  BUTTON: 'wizard button',
} as const satisfies Record<string, WizardOpenSource>;

/**
 * storeType is not based on store but actually the
 * user's selected search preference set in the store switcher
 */
export type StoreType = 'recreational' | 'medical' | 'all';

export type AuthSource = 'account landing page' | 'bag' | 'checkout';

interface BaseMixpanelEvent {
  event: (typeof EventNames)[keyof typeof EventNames];
}

type MixpanelEventProperties<K extends keyof Properties> = Required<
  Pick<Properties, K>
>;

interface Properties {
  aggregateRatingMin: number | undefined;
  appliedFilters: string[];
  brandId?: Nullable<number>;
  cardLocation: WizardCardLocation;
  cartId: Nullable<number> | undefined;
  'Default Tab': string;
  discountDetails?: number;
  discountType?: string;
  enabled: boolean;
  experiment: string;
  filterCount: number;
  filters: string[];
  'Google SSO enabled?': boolean;
  hasMultipleWeights: boolean;
  'Header Element Selected':
    | 'logo'
    | 'bag icon'
    | 'search icon'
    | 'profile icon'
    | 'hamburger menu';
  hits: number;
  index: number;
  isOutOfStock: boolean;
  'Menu Option Selected': string;
  pageTitle: string | undefined;
  path: string;
  pricePerUnit: Nullable<number>;
  priceRange: string | undefined;
  priceTotal: Nullable<number>;
  productBrand?: Nullable<string>;
  productId: number;
  productKind?: string;
  productLineage?: Nullable<string>;
  productName: string;
  quantity: number;
  query: string;
  reservationMode:
    | 'pickup'
    | 'delivery'
    /** Included for compatibility with orders placed elsewhere */
    | 'retail'
    | 'curbside'
    | 'kiosk'
    | undefined;
  search: string;
  selectedFilters: string[];
  'Sign In Method': 'email' | 'google';
  skippedFilters: string[];
  sort: string;
  Source: AuthSource | 'pdp' | 'specials row';
  specialId?: number;
  specialName?: string;
  specialRankOnList: number;
  specialType: SpecialType;
  specialTypes: Nullable<SpecialType>;
  'Star Rating': number;
  storeListIndex: number | undefined;
  storeType: StoreType;
  totalProducts: number;
  wizardSource: WizardOpenSource;
}

interface AbTestEvent
  extends BaseMixpanelEvent, MixpanelEventProperties<'enabled' | 'experiment'> {
  event: EventNames.AbTest;
}

interface AddedProductToCartEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      | 'brandId'
      | 'cartId'
      | 'hasMultipleWeights'
      | 'pricePerUnit'
      | 'priceTotal'
      | 'productBrand'
      | 'productId'
      | 'productKind'
      | 'productLineage'
      | 'productName'
      | 'quantity'
      | 'reservationMode'
      | 'storeType'
    >,
    Partial<MixpanelEventProperties<'cardLocation' | 'index'>> {
  event: EventNames.AddedProductToCart;
}

interface ProductListingTapEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      | 'brandId'
      | 'hasMultipleWeights'
      | 'pricePerUnit'
      | 'productBrand'
      | 'productId'
      | 'productKind'
      | 'productLineage'
      | 'productName'
      | 'storeType'
      | 'reservationMode'
      | 'specialTypes'
    >,
    Partial<MixpanelEventProperties<'cardLocation' | 'index'>> {
  event: EventNames.ProductListingTap;
}

interface MenuTapEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      'reservationMode' | 'Default Tab' | 'Menu Option Selected'
    > {
  event: EventNames.MenuTap;
}

interface CustomerRatingEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      'cartId' | 'reservationMode' | 'storeType' | 'Star Rating'
    > {
  event: EventNames.CustomerRating;
}

interface LoggedInEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      'Google SSO enabled?' | 'Sign In Method' | 'Source'
    > {
  event: EventNames.LoggedIn;
}

interface RegisteredEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      'Google SSO enabled?' | 'Sign In Method' | 'Source'
    > {
  event: EventNames.Registered;
}

interface HeaderTapEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<'Header Element Selected' | 'reservationMode'> {
  event: EventNames.HeaderTap;
}
interface StoreSetEvent
  extends BaseMixpanelEvent, MixpanelEventProperties<'storeListIndex'> {
  event: EventNames.StoreSet;
}

interface StoreSpecialTapEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      'Source' | 'specialId' | 'specialRankOnList' | 'specialType'
    > {
  event: EventNames.StoreSpecialTap;
}

interface SearchedMenuProductsEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      | 'filters'
      | 'filterCount'
      | 'hits'
      | 'sort'
      | 'query'
      | 'priceRange'
      | 'aggregateRatingMin'
    > {
  event: EventNames.SearchedMenuProducts;
}

interface StoreSpecialPromptTapEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      | 'discountDetails'
      | 'discountType'
      | 'Source'
      | 'specialId'
      | 'specialName'
      | 'specialType'
    > {
  event: EventNames.StoreSpecialPromptTap;
}

interface ViewedProductEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      | 'isOutOfStock'
      | 'productBrand'
      | 'productId'
      | 'productKind'
      | 'productName'
    > {
  event: EventNames.ViewedProduct;
}

interface PageViewedEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<'path' | 'search'>,
    Partial<MixpanelEventProperties<'pageTitle'>> {
  event: EventNames.PageViewed;
}

interface WizardOpenedEvent
  extends BaseMixpanelEvent, MixpanelEventProperties<'wizardSource'> {
  event: EventNames.WizardOpened;
}

export type BloomCalloutVariants =
  | 'activity'
  | 'cart'
  | 'checkout'
  | 'jane gold'
  | 'my info'
  | 'pay by link';

interface LinkBankAccountEvent extends BaseMixpanelEvent {
  email: string;
  event:
    | EventNames.LinkBankAccountFailed
    | EventNames.LinkedFirstBankAccount
    | EventNames.LinkedNewBankAccount
    | EventNames.UnlinkedLastBankAccount;
  isJanePay?: boolean;
  source?: BloomCalloutVariants;
  username: string;
}

interface WizardSelectionConfirmedEvent
  extends BaseMixpanelEvent, MixpanelEventProperties<'selectedFilters'> {
  event: EventNames.WizardSelectionConfirmed;
}

interface WizardResultsViewedEvent
  extends
    BaseMixpanelEvent,
    MixpanelEventProperties<
      'appliedFilters' | 'skippedFilters' | 'totalProducts' | 'selectedFilters'
    > {
  event: EventNames.WizardResultsViewed;
}

export type MixpanelEvent =
  | AbTestEvent
  | AddedProductToCartEvent
  | CustomerRatingEvent
  | HeaderTapEvent
  | LinkBankAccountEvent
  | LoggedInEvent
  | MenuTapEvent
  | PageViewedEvent
  | ProductListingTapEvent
  | RegisteredEvent
  | StoreSetEvent
  | StoreSpecialTapEvent
  | SearchedMenuProductsEvent
  | StoreSpecialPromptTapEvent
  | ViewedProductEvent
  | WizardOpenedEvent
  | WizardResultsViewedEvent
  | WizardSelectionConfirmedEvent;
